/**
 * Runtime configuration for the SPA.
 *
 * WHY THIS EXISTS, rather than `import.meta.env.VITE_API_URL`:
 * Vite inlines `import.meta.env.*` at BUILD time. Baking the API origin into
 * the bundle would mean the artifact that passed testing in staging is not the
 * artifact that ships to production — each environment needs its own build,
 * and "promote the tested build" stops being possible. Fetching a tiny
 * `config.json` at startup keeps one immutable artifact for every environment;
 * the deployment overwrites that one file.
 *
 * DEFAULT IS SAME-ORIGIN (`/api/v1`), and that is the intended production
 * shape: Azure Static Web Apps is configured with the API container app as a
 * linked backend, so `/api/*` on the SPA's own hostname is proxied to it. That
 * keeps the session cookie first-party — no cross-site cookie, no `SameSite=None`,
 * no CORS preflight on every request. `apiBaseUrl` exists as an absolute-URL
 * escape hatch for deployments that cannot use a linked backend; choosing it
 * also means accepting `SameSite=None; Secure` cookies (see docs/AZURE_DEPLOYMENT.md).
 *
 * Failure is deliberately non-fatal: a missing or malformed config.json falls
 * back to same-origin rather than showing the user a blank page, because
 * same-origin is correct for the default deployment anyway.
 */

export interface RuntimeConfig {
  /** Absolute origin+path, or a same-origin path. No trailing slash. */
  readonly apiBaseUrl: string;
  /** Shown in the UI so an operator can tell staging from production at a glance. Empty hides it. */
  readonly environmentLabel: string;
  /**
   * Where this deployment is hosted, shown beside the cloud glyph in the
   * sidebar footer (e.g. "Azure", "On-prem VM"). Display-only branding —
   * never a health claim; the status line under it comes from the real
   * readiness check. Blank/missing falls back to "Azure".
   */
  readonly hostingLabel: string;
  /**
   * Product name shown in the SPA (sidebar, page titles, sign-in and the
   * public confirm page). Set it to the same value as the worker's
   * EMAIL_BRAND_NAME so the emails and the pages they link to match.
   * Blank/missing falls back to "ResolveIT".
   */
  readonly brandName: string;
  /**
   * IANA time zone every timestamp is shown in, whatever the browser's zone is
   * (see `lib/time.ts`). Set it to the same value as the API/worker
   * APP_TIME_ZONE. Blank/missing/invalid falls back to "Asia/Kolkata" (IST).
   */
  readonly timeZone: string;
}

export const DEFAULT_TIME_ZONE = 'Asia/Kolkata';

export const DEFAULT_BRAND_NAME = 'ResolveIT';

const DEFAULT_CONFIG: RuntimeConfig = {
  apiBaseUrl: '/api/v1',
  environmentLabel: '',
  hostingLabel: 'Azure',
  brandName: DEFAULT_BRAND_NAME,
  timeZone: DEFAULT_TIME_ZONE,
};

let current: RuntimeConfig = DEFAULT_CONFIG;

/** The resolved API base. Valid immediately; accurate once `loadRuntimeConfig()` has resolved. */
export function apiBaseUrl(): string {
  return current.apiBaseUrl;
}

export function runtimeConfig(): RuntimeConfig {
  return current;
}

/** The configured product name (runtime config `brandName`, default "ResolveIT"). */
export function brandName(): string {
  return current.brandName || DEFAULT_BRAND_NAME;
}

/** The configured display time zone (runtime config `timeZone`, default "Asia/Kolkata"). */
export function appTimeZone(): string {
  return current.timeZone || DEFAULT_TIME_ZONE;
}

function validTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || !tz.trim()) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz.trim() });
    return true;
  } catch {
    return false;
  }
}

/** Exposed for tests only — resets module state between cases. */
export function __setRuntimeConfigForTests(config: Partial<RuntimeConfig>): void {
  current = { ...DEFAULT_CONFIG, ...config };
}

function normalise(raw: unknown): RuntimeConfig {
  if (typeof raw !== 'object' || raw === null) return DEFAULT_CONFIG;
  const candidate = raw as Partial<Record<keyof RuntimeConfig, unknown>>;

  let apiBase = DEFAULT_CONFIG.apiBaseUrl;
  if (typeof candidate.apiBaseUrl === 'string' && candidate.apiBaseUrl.trim()) {
    const trimmed = candidate.apiBaseUrl.trim().replace(/\/+$/, '');
    // Refuse plain http:// from an https:// page rather than letting the
    // browser block every request as mixed content with no explanation.
    if (trimmed.startsWith('http://') && window.location.protocol === 'https:') {
      // eslint-disable-next-line no-console
      console.error(
        `Ignoring apiBaseUrl "${trimmed}" from config.json: an https page cannot call a plain http API (mixed content). ` +
          'Falling back to same-origin.',
      );
    } else {
      apiBase = trimmed;
    }
  }

  return {
    apiBaseUrl: apiBase,
    environmentLabel: typeof candidate.environmentLabel === 'string' ? candidate.environmentLabel : '',
    hostingLabel:
      typeof candidate.hostingLabel === 'string' && candidate.hostingLabel.trim()
        ? candidate.hostingLabel.trim().slice(0, 40)
        : DEFAULT_CONFIG.hostingLabel,
    brandName:
      typeof candidate.brandName === 'string' && candidate.brandName.trim()
        ? candidate.brandName.trim().slice(0, 80)
        : DEFAULT_CONFIG.brandName,
    timeZone: validTimeZone(candidate.timeZone) ? candidate.timeZone.trim() : DEFAULT_TIME_ZONE,
  };
}

/**
 * Fetches `config.json` once, before the app renders. `cache: 'no-store'` is
 * required: without it a CDN-cached config.json outlives a re-point of the API
 * and the SPA keeps calling the old origin.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' });
    if (!response.ok) throw new Error(`config.json responded ${response.status}`);
    current = normalise(await response.json());
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      `Could not load /config.json (${err instanceof Error ? err.message : String(err)}). ` +
        'Falling back to same-origin API at /api/v1.',
    );
    current = DEFAULT_CONFIG;
  }
  return current;
}
